"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
/**
 * StudentPlanIframePage - Iframe-embeddable student edit page
 *
 * Route: /degree-planning/student/edit?studentProgramId=[id]
 * Rendered OUTSIDE MainLayout (no sidebar, no header) - it's an iframe target.
 *
 * Authentication modes:
 *
 * 1. Salesforce iframe: Salesforce passes a JWT via `?token=<jwt>`. On mount the token
 *    is applied as a page-scoped Authorization override on the global apiClient and the
 *    param is immediately stripped from the URL via history.replaceState to limit
 *    exposure in browser history and Referer headers. Global auth state (AuthProvider)
 *    and sessionStorage are NOT touched — the override is cleared automatically when
 *    this component unmounts, so no other route is ever affected.
 *
 * 2. Standard session: If no `?token=` is present, falls back to sessionStorage JWT
 *    auth via AuthProvider. Unauthenticated users are redirected to login and returned
 *    here after login (location state carries the full path + search string).
 */
const react_1 = require("react");
const react_router_dom_1 = require("react-router-dom");
const AuthProvider_1 = require("../../providers/AuthProvider");
const apiClient_1 = require("../../helpers/apiClient");
const permissions_1 = require("../../constants/permissions");
const TermPlanningGrid_1 = require("../../components/TermPlanningGrid");
const Button_1 = require("../../components/Button");
const LoadingSpinner_1 = require("../../components/LoadingSpinner");
const ValidationErrors_1 = require("../../components/ValidationErrors");
const useStudentPlanEditor_1 = require("../../hooks/useStudentPlanEditor");
require("./StudentPlanIframePage.scss");
function classifyError(loadError, studentProgramId, isDeleted) {
    if (isDeleted) {
        return {
            kind: 'deleted',
            icon: '✓',
            title: 'Degree Plan Deleted',
            body: 'This degree plan has been successfully deleted.',
        };
    }
    if (!studentProgramId) {
        return {
            kind: 'param',
            icon: '🔗',
            title: 'Missing Student Program ID',
            body: 'This view requires a studentProgramId URL parameter. Please reopen this panel from Salesforce, or contact your administrator if the problem persists.',
            detail: 'Expected URL: ...?studentProgramId=<uuid>',
        };
    }
    const msg = loadError !== null && loadError !== void 0 ? loadError : '';
    if (msg.toLowerCase().includes('unauthorized') ||
        msg.toLowerCase().includes('invalid or expired token') ||
        msg.toLowerCase().includes('token')) {
        return {
            kind: 'auth',
            icon: '🔒',
            title: 'Authentication Failed',
            body: 'Your session token is missing, invalid, or has expired. Please close this panel and reopen it from Salesforce to get a fresh session token.',
            detail: msg,
        };
    }
    if (msg.toLowerCase().includes('no degree plan found') ||
        msg.toLowerCase().includes('not found') ||
        msg.toLowerCase().includes('resource not found')) {
        return {
            kind: 'missing',
            icon: '📋',
            title: 'Degree Plan Not Found',
            body: 'No degree plan was found for this student program. The plan may not exist yet, may have been deleted, or the Student Program ID may be incorrect.',
            detail: `Student Program ID: ${studentProgramId}`,
        };
    }
    if (msg.toLowerCase().includes('missing required parameter') ||
        msg.toLowerCase().includes('studentprogramid')) {
        return {
            kind: 'param',
            icon: '🔗',
            title: 'Missing Student Program ID',
            body: 'This view requires a studentProgramId URL parameter. Please reopen this panel from Salesforce, or contact your administrator if the problem persists.',
            detail: msg,
        };
    }
    if (msg.toLowerCase().includes('network error') ||
        msg.toLowerCase().includes('check your connection') ||
        msg.toLowerCase().includes('failed to fetch')) {
        return {
            kind: 'network',
            icon: '🌐',
            title: 'Connection Error',
            body: 'Unable to reach the server. Please check your internet connection and try reloading this page.',
            canRetry: true,
        };
    }
    if (msg.toLowerCase().includes('timeout') || msg.toLowerCase().includes('request timeout')) {
        return {
            kind: 'server',
            icon: '⏱',
            title: 'Request Timed Out',
            body: 'The server took too long to respond. Please try reloading this page. If the problem persists, contact your administrator.',
            canRetry: true,
        };
    }
    if (msg.toLowerCase().includes('server error') || msg.toLowerCase().includes('try again later')) {
        return {
            kind: 'server',
            icon: '⚠',
            title: 'Server Error',
            body: 'Something went wrong on our end. Please try again in a few moments. If the problem persists, contact your administrator.',
            detail: msg,
            canRetry: true,
        };
    }
    return {
        kind: 'generic',
        icon: '⚠',
        title: 'Unable to Load Degree Plan',
        body: 'An unexpected error occurred while loading the degree plan. Please try reloading this page or contact your administrator.',
        detail: msg || undefined,
        canRetry: true,
    };
}
// ---------------------------------------------------------------------------
// Error card component
// ---------------------------------------------------------------------------
const IframeErrorCard = ({ info }) => ((0, jsx_runtime_1.jsx)("div", { className: "iframe-plan__error-wrap", children: (0, jsx_runtime_1.jsxs)("div", { className: `iframe-plan__error-card iframe-plan__error-card--${info.kind}`, children: [(0, jsx_runtime_1.jsx)("span", { className: "iframe-plan__error-icon", "aria-hidden": "true", children: info.icon }), (0, jsx_runtime_1.jsx)("h1", { className: "iframe-plan__error-title", children: info.title }), (0, jsx_runtime_1.jsx)("p", { className: "iframe-plan__error-body", children: info.body }), info.detail && ((0, jsx_runtime_1.jsx)("pre", { className: "iframe-plan__error-detail", children: info.detail })), info.canRetry && ((0, jsx_runtime_1.jsx)("div", { className: "iframe-plan__error-actions", children: (0, jsx_runtime_1.jsx)(Button_1.Button, { onClick: () => window.location.reload(), variant: "secondary", children: "Reload" }) }))] }) }));
// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------
const StudentPlanIframePage = () => {
    const [searchParams] = (0, react_router_dom_1.useSearchParams)();
    const studentProgramId = searchParams.get('studentProgramId');
    const { isAuthenticated, loading: authLoading, hasPermission } = (0, AuthProvider_1.useAuth)();
    const location = (0, react_router_dom_1.useLocation)();
    // Capture the Salesforce-provided token from the URL exactly once at mount.
    // useState initializer runs only on the first render, before any effects, so
    // the value is stable even after we strip the param from the URL below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const [urlToken] = (0, react_1.useState)(() => searchParams.get('token'));
    // Track whether the plan was just deleted so we can show a confirmation state
    // instead of an error card.
    const [isDeleted, setIsDeleted] = (0, react_1.useState)(false);
    // Apply URL token as a page-scoped apiClient override.
    // - Does NOT modify AuthProvider state or sessionStorage.
    // - Cleared automatically on unmount so no other route is affected.
    // - Strips the token from the browser URL to reduce exposure in history/Referer.
    (0, react_1.useEffect)(() => {
        if (!urlToken)
            return;
        apiClient_1.apiClient.setTokenOverride(urlToken);
        const stripped = new URL(window.location.href);
        stripped.searchParams.delete('token');
        window.history.replaceState({}, '', stripped.toString());
        return () => {
            apiClient_1.apiClient.setTokenOverride(null);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps -- urlToken is stable (captured at mount)
    const { degreePlan, availableTerms, availableCourses, termPlans, setTermPlans, graduationDate, offSequence, isDirty, loading, saving, exporting, deleting, showDeleteConfirm, setShowDeleteConfirm, loadError, saveValidationErrors, handleToggleFailed, handleSave, handleExport, handleDelete: _handleDelete, } = (0, useStudentPlanEditor_1.useStudentPlanEditor)({
        studentProgramId,
        // Proceed if authenticated via standard session OR via Salesforce URL token.
        canLoad: isAuthenticated || !!urlToken,
    });
    // Permission check for iframe context — no redirect since this is an embedded view.
    // Auth must have resolved before we check (authLoading=false or urlToken present).
    const authResolved = !!urlToken || !authLoading;
    // Salesforce URL tokens bypass the permissions array — access is granted by the
    // token itself and enforced by the backend on every API call. Only check the
    // permissions array for standard-session users.
    const canEditStudentPlan = !!urlToken || hasPermission(permissions_1.PERMISSIONS.DEGREE_PLAN_EDIT);
    // Wrap handleDelete so we can set the deleted state for the confirmation card
    const handleDelete = () => __awaiter(void 0, void 0, void 0, function* () {
        yield _handleDelete();
        setIsDeleted(true);
    });
    // Standard-session auth gates — skipped entirely when a URL token is present,
    // since the apiClient override handles auth for the Salesforce iframe path.
    if (!urlToken) {
        if (authLoading) {
            return ((0, jsx_runtime_1.jsx)("div", { className: "iframe-plan", children: (0, jsx_runtime_1.jsx)("div", { className: "iframe-plan__loading", children: (0, jsx_runtime_1.jsx)(LoadingSpinner_1.LoadingSpinner, { label: "Loading..." }) }) }));
        }
        if (!isAuthenticated) {
            return (0, jsx_runtime_1.jsx)(react_router_dom_1.Navigate, { to: "/login", state: { from: location }, replace: true });
        }
    }
    // Inline permission denial — shown after auth resolves, before the plan loads.
    // No redirect in iframe context; the parent page must not navigate away.
    if (authResolved && !canEditStudentPlan) {
        return ((0, jsx_runtime_1.jsx)("div", { className: "iframe-plan", children: (0, jsx_runtime_1.jsx)(IframeErrorCard, { info: {
                    kind: 'auth',
                    icon: '🔒',
                    title: 'Access Denied',
                    body: 'You do not have permission to edit student degree plans. Please contact your administrator if you believe this is an error.',
                } }) }));
    }
    if (loading) {
        return ((0, jsx_runtime_1.jsx)("div", { className: "iframe-plan", children: (0, jsx_runtime_1.jsx)("div", { className: "iframe-plan__loading", children: (0, jsx_runtime_1.jsx)(LoadingSpinner_1.LoadingSpinner, { label: "Loading degree plan..." }) }) }));
    }
    // Show the "plan was just deleted" success card
    if (isDeleted && !degreePlan) {
        return ((0, jsx_runtime_1.jsx)("div", { className: "iframe-plan", children: (0, jsx_runtime_1.jsx)(IframeErrorCard, { info: classifyError(null, studentProgramId, true) }) }));
    }
    // Show a classified error card for any load failure
    if (loadError || !degreePlan) {
        const errorInfo = classifyError(loadError, studentProgramId);
        return ((0, jsx_runtime_1.jsx)("div", { className: "iframe-plan", children: (0, jsx_runtime_1.jsx)(IframeErrorCard, { info: errorInfo }) }));
    }
    return ((0, jsx_runtime_1.jsxs)("div", { className: "iframe-plan", children: [(0, jsx_runtime_1.jsxs)("div", { className: "iframe-plan__content", children: [(0, jsx_runtime_1.jsx)("div", { className: "iframe-plan__content-header", children: (0, jsx_runtime_1.jsx)("h2", { className: "iframe-plan__content-title", children: "Edit Student Degree Plan" }) }), (0, jsx_runtime_1.jsxs)("div", { className: "iframe-plan__meta", children: [(0, jsx_runtime_1.jsxs)("span", { className: "iframe-plan__meta-label", children: [(0, jsx_runtime_1.jsx)("span", { className: "iframe-plan__meta-key", children: "Graduation Date" }), (0, jsx_runtime_1.jsx)("span", { className: "iframe-plan__meta-value", children: graduationDate
                                            ? new Date(graduationDate + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                                            : '\u2014' })] }), (0, jsx_runtime_1.jsxs)("span", { className: "iframe-plan__meta-label", children: [(0, jsx_runtime_1.jsx)("span", { className: "iframe-plan__meta-key", children: "Off Sequence" }), (0, jsx_runtime_1.jsx)("span", { className: "iframe-plan__meta-value", children: offSequence ? 'Yes' : 'No' })] })] }), (0, jsx_runtime_1.jsx)(TermPlanningGrid_1.TermPlanningGrid, { termPlans: termPlans, availableTerms: availableTerms, availableCourses: availableCourses, onChange: setTermPlans, onToggleFailed: handleToggleFailed }), saveValidationErrors.length > 0 && ((0, jsx_runtime_1.jsx)(ValidationErrors_1.ValidationErrors, { errors: saveValidationErrors.map((msg, i) => ({ id: `save-validation-${i}`, message: msg })), title: "Cannot update degree plan" })), (0, jsx_runtime_1.jsxs)("div", { className: "iframe-plan__actions", children: [(0, jsx_runtime_1.jsx)(Button_1.Button, { onClick: handleExport, loading: exporting, disabled: exporting, variant: "secondary", children: "Export PDF" }), (0, jsx_runtime_1.jsx)("span", { title: !isDirty && !saving ? 'No changes to save — edit the plan to enable Update' : undefined, style: { display: 'inline-flex' }, children: (0, jsx_runtime_1.jsx)(Button_1.Button, { onClick: handleSave, loading: saving, disabled: saving || !isDirty, children: "Update" }) }), (0, jsx_runtime_1.jsx)(Button_1.Button, { onClick: () => setShowDeleteConfirm(true), variant: "danger", disabled: deleting, children: "Delete" })] })] }), showDeleteConfirm && ((0, jsx_runtime_1.jsx)("div", { className: "iframe-plan__confirm-backdrop", children: (0, jsx_runtime_1.jsxs)("div", { className: "iframe-plan__confirm-dialog", role: "dialog", "aria-modal": "true", "aria-labelledby": "confirm-title", children: [(0, jsx_runtime_1.jsx)("h3", { className: "iframe-plan__confirm-title", id: "confirm-title", children: "Confirm Delete" }), (0, jsx_runtime_1.jsx)("p", { className: "iframe-plan__confirm-body", children: "Are you sure you want to delete this degree plan? This action cannot be undone. You will not be able to reapply the same cohort plan." }), (0, jsx_runtime_1.jsxs)("div", { className: "iframe-plan__confirm-actions", children: [(0, jsx_runtime_1.jsx)(Button_1.Button, { onClick: () => setShowDeleteConfirm(false), variant: "secondary", children: "Cancel" }), (0, jsx_runtime_1.jsx)(Button_1.Button, { onClick: handleDelete, variant: "danger", loading: deleting, children: "Delete" })] })] }) }))] }));
};
exports.default = StudentPlanIframePage;
